/**
 * Core Swedish dictionary — the primary language (spec §4.8).
 * Features add their own keys through their `dict` export; prefix them with the
 * feature id (`practice.start`) so the merged dictionary never collides.
 */
export const sv = {
  "privacy.link": "Integritet och personuppgifter",
  "auth.registrationGuidance":
    "Om du har fått en registreringskod av läraren anger du den när du skapar kontot.",
  "courseStart.title": "Modern fysik HT2026 – börja här",
  "courseStart.book":
    "Kursboken är Young & Freedman, University Physics, 15:e uppl., kapitel 37–44. Webbplatsens moment 1–9 följer kursens indelning och motsvarar inte bokens kapitelnummer. Övningsbanken täcker ännu inte alla dessa kapitel.",
  "courseStart.accounts":
    "Du loggar in separat i Canvas och på den här webbplatsen. Resultaten överförs inte automatiskt till Canvas.",
  "courseStart.registration":
    "När du skapar ditt konto här med Google anger du ett namn som läraren känner igen. Använd samma Google-konto när du loggar in igen.",
  "courseStart.continue": "Fortsätt till {lecture}",
  "courseStart.selection":
    "Länken går till övningarna för {lecture}. Kontrollera urvalet innan du väljer Starta. Övningar för senare föreläsningar publiceras efter hand.",
  "courseStart.open": "Öppna föreläsningens övningar",
  "courseStart.reporting":
    "Det går ännu inte att få rapporter per föreläsning. Att du har övat visar varken att du har deltagit i föreläsningen eller att du kan lösa en ny uppgift utan hjälp.",
  "courseStart.delete":
    "I Inställningar kan du radera ditt konto och de uppgifter som sparats i det.",
  "courseStart.studyTitle": "Så använder du övningarna",
  "courseStart.study":
    "Försök lösa uppgiften på egen hand innan du använder ledtrådarna och jämför ditt resonemang med lösningen. Återkom till övningarna en annan dag.",
  "courseStart.notFound": "Kursen eller föreläsningen hittades inte. Kontrollera länken i Canvas.",

  "app.title": "Modern fysik",
  "app.subtitle": "Övning inför tentan",
  "app.skipToContent": "Hoppa till innehållet",

  "nav.home": "Start",
  "nav.settings": "Inställningar",
  "nav.teacher": "Lärarvy",
  "nav.signOut": "Logga ut",
  "nav.openMenu": "Öppna menyn",
  "nav.closeMenu": "Stäng menyn",
  "nav.account": "Konto",

  "common.loading": "Laddar …",
  "common.save": "Spara",
  "common.saving": "Sparar …",
  "common.saved": "Sparat",
  "common.cancel": "Avbryt",
  "common.close": "Stäng",
  "practice.setup.taxonomyError": "Kunde inte hämta ämnen och kursmoment.",
  "common.retry": "Försök igen",
  "examGuard.checkFailed.title": "Det gick inte att kontrollera om en tenta pågår",
  "examGuard.checkFailed.body":
    "Servern svarade inte i tid. Sidan hålls låst tills vi vet att ingen övningstenta pågår.",
  "common.back": "Tillbaka",
  "common.next": "Nästa",
  "common.delete": "Radera",
  "common.error": "Något gick fel",
  "common.nothingHere": "Inget att visa ännu",
  "common.language": "Språk",
  "common.theme": "Tema",
  "common.theme.light": "Ljust",
  "common.theme.dark": "Mörkt",
  "common.scrollableContent": "Rullbart innehåll",
  "common.confidence.question": "Hur säker är du?",
  "common.confidence.1": "Gissar",
  "common.confidence.2": "Osäker",
  "common.confidence.3": "Ganska säker",
  "common.confidence.4": "Helt säker",

  "auth.email": "E-post",
  "auth.displayName": "Namn",
  "auth.signIn": "Logga in",
  "auth.signUp": "Skapa konto",
  "auth.registrationCode": "Registreringskod",
  "auth.teacherCode": "Lärarkod",
  "auth.privacyTitle": "Vad läraren ser",
  "auth.privacyBody":
    "Läraren ser ditt namn, din e-postadress och din statistik i kursen: vilka uppgifter du övat på, dina poäng och dina resultat på övningstentor. Läraren ser inte vad du skriver i anteckningsfälten. Du loggar in med Google och har inget lösenord hos oss. Från Google sparar vi bara din e-postadress och ett konto-id. Du behöver ett Google-konto för att använda portalen. Ett gratis konto räcker, och du kan skapa det med vilken e-postadress som helst. Du kan radera ditt konto när du vill under Inställningar.",
  "auth.signedInAs": "Inloggad som {name}",
  "auth.rateLimitedRetry.seconds": "För många försök. Försök igen om {seconds} s.",
  "auth.rateLimitedRetry.minutes.one": "För många försök. Försök igen om {minutes} minut.",
  "auth.rateLimitedRetry.minutes.other": "För många försök. Försök igen om {minutes} minuter.",
  "auth.google.signIn": "Logga in med Google",
  "auth.google.disabled": "Inloggningen är tillfälligt avstängd. Försök igen senare.",
  "auth.google.cancelled": "Inloggningen med Google avbröts.",
  "auth.google.expired":
    "Inloggningen tog för lång tid eller startades i en annan flik. Försök igen.",
  "auth.google.unverified":
    "Din e-postadress är inte verifierad hos Google. Verifiera den där och försök igen.",
  "auth.google.failed": "Det gick inte att logga in med Google just nu. Försök igen om en stund.",
  "auth.google.conflict":
    "Det finns redan ett konto med den här e-postadressen, kopplat till ett annat Google-konto. Kontakta läraren.",
  "auth.google.rateLimited":
    "För många misslyckade inloggningsförsök från det här nätverket. Försök igen om en stund.",
  "auth.google.completeTitle": "Skapa konto",
  "auth.google.completeAs": "Du skapar ett konto som {email}.",
  "auth.google.iAmTeacher": "Jag är lärare",
  "auth.google.pendingExpired": "Det tog för lång tid. Börja om med Google.",
  "auth.google.notYou": "Inte du? Börja om",
  "auth.google.firstTime": "Första gången? Logga in med Google så skapar vi kontot direkt efter.",
  "auth.google.sharedComputer":
    "Använder du en delad dator? Logga ut från Google också när du är klar.",
  "auth.code.rateLimited": "För många felaktiga koder. Vänta en stund och försök igen.",
  "auth.privacyGoogle":
    "Du loggar in med Google. Vi får bara din e-postadress och ett konto-id från Google — inget lösenord och inget annat från ditt Google-konto. Du behöver ett Google-konto. Ett gratis konto räcker, och du kan skapa det med vilken e-postadress som helst.",

  "landing.heading": "Övningsportal till Modern fysik",
  "landing.body":
    "Uppgifter per moment, övningstentor i samma format som den riktiga tentan, begreppskort med repetition och en tydlig bild av vad du behöver träna mest på.",
  "landing.ctaRegister": "Skapa konto",
  "landing.ctaLogin": "Logga in",
  "landing.codes": "LGFY41 / L9FY41 / L9FY45",
  "landing.course": "Modern fysik, LGFY41 / L9FY41 / L9FY45 — Göteborgs universitet",

  "home.greeting": "Hej {name}!",
  "home.subtitle": "Vad vill du göra idag?",
  "home.quickLinks": "Snabbstart",
  "home.count.practice": "{count} frågor att öva på",
  "home.count.exam": "{count} gamla tentor att skriva",
  "home.count.flashcards.due.one": "{count} kort att repetera",
  "home.count.flashcards.due.other": "{count} kort att repetera",
  "home.count.flashcards.new.one": "{count} nytt",
  "home.count.flashcards.new.other": "{count} nya",
  "home.count.library": "{topics} ämnen · {formulas} formler",
  "home.teacherCard": "Lärarvy",
  "home.teacherCardBody": "Klassens statistik, rapporter och roller.",
  "home.activeExam.title": "Pågående övningstenta",
  "home.activeExam.body": "Fortsätt där du var, eller lämna in det du hunnit med.",
  "home.activeExam.link": "Fortsätt övningstentan",

  "settings.title": "Inställningar",
  "settings.account": "Konto",
  "settings.preferences": "Inställningar för studier",
  "settings.newCardsPerDay": "Nya begreppskort per dag",
  "settings.bonusPoints": "Bonuspoäng från inlämningar",
  "settings.bonusHelp":
    "Bonuspoäng räknas bara mot G och bara när råpoängen är högst 2 p under G-gränsen.",
  "settings.dangerZone": "Radera konto",
  "settings.deleteWarning":
    "Kontot och all din historik raderas permanent. Det går inte att ångra.",
  "settings.deleteAccount": "Radera mitt konto",
  "settings.deleted": "Kontot är raderat.",
  "settings.teacher.title": "Jag är lärare",
  "settings.teacher.submit": "Bli lärare",
  "settings.google.reauth":
    "Av säkerhetsskäl behöver du logga in med Google igen innan du kan radera kontot.",
  "settings.google.reauthButton": "Logga in med Google igen",
  "settings.google.reauthMismatch":
    "Det var ett annat Google-konto än ditt. Försök igen och välj rätt konto.",

  "nav.help": "Hjälp",

  "home.examMode.badge": "Tentaläge",
  "home.examMode.daysLeft.exam.one": "{count} dag kvar till tentan ({date})",
  "home.examMode.daysLeft.exam.other": "{count} dagar kvar till tentan ({date})",
  "home.examMode.daysLeft.retake.one": "{count} dag kvar till omtentan ({date})",
  "home.examMode.daysLeft.retake.other": "{count} dagar kvar till omtentan ({date})",
  "home.examMode.today.exam": "Tentan är idag ({date}). Lycka till!",
  "home.examMode.today.retake": "Omtentan är idag ({date}). Lycka till!",
  "home.examMode.reviewingNoDate": "Du repeterar inför tentan",
  "home.examMode.reviewing": "Du har valt att du repeterar inför tentan.",
  "home.examMode.settings": "Ändra under Inställningar",
  "home.examMode.step.exam": "1. Skriv en gammal tenta på tid",
  "home.examMode.step.examHint":
    "Samma tid och hjälpmedel som på riktigt. Rätta sedan med bedömningsmallen.",
  "home.examMode.step.examCta": "Skriv {exam}",
  "home.examMode.step.weak": "2. Öva på dina svagaste ämnen",
  "home.examMode.step.weakEmpty": "När du har övat lite syns dina svagaste ämnen här.",
  "home.examMode.step.weakAll": "Alla svagaste ämnen i Framsteg",
  "home.examMode.step.drill": "3. Tidsatt övning",
  "home.examMode.step.formulas": "4. Formelsamlingen",
  "home.examMode.step.formulasHint": "Samma formler som du har med dig på tentan.",
  "home.examMode.step.formulasCta": "Öppna formelsamlingen",
  "home.examMode.note": "I tentaläget övar du på allt som är öppet, inte bara det vi gått igenom.",
  "onboarding.title": "Kom igång – 30 sekunder",
  "onboarding.intro":
    "Fyra snabba frågor så att dagens pass passar dig. Allt går att ändra senare under Inställningar.",
  "onboarding.position.legend": "Var är du i kursen?",
  "onboarding.position.following": "Jag följer föreläsningarna",
  "onboarding.position.missed": "Jag har missat föreläsningar",
  "onboarding.position.reviewing": "Jag repeterar inför tentan",
  "onboarding.position.next": "Nästa föreläsning: {lecture}",
  "onboarding.position.last": "Senaste föreläsningen: {lecture}",
  "onboarding.missed.legend": "Vilka missade du? De kommer först i dagens pass.",
  "onboarding.missed.none": "Inga föreläsningar har hållits än.",
  "onboarding.goal.legend": "Vilket betyg siktar du på?",
  "onboarding.goal.G": "G",
  "onboarding.goal.VG": "VG",
  "onboarding.goal.unknown": "Vet inte än",
  "onboarding.minutes.legend": "Hur mycket tid har du per dag?",
  "onboarding.minutes.10": "10 min",
  "onboarding.minutes.20": "20 min",
  "onboarding.minutes.30": "30 min eller mer",
  "onboarding.math.legend": "Hur säker känner du dig på matematiken (algebra, potenser, enheter)?",
  "onboarding.math.sure": "Säker",
  "onboarding.math.rusty": "Lite ringrostig",
  "onboarding.math.unsure": "Osäker",
  "onboarding.glossary.title": "Ord du kommer att se",
  "onboarding.glossary.moment":
    "Moment: kursens stora ämnesblock, till exempel relativitet eller atomfysik. Varje moment hör till några föreläsningar.",
  "onboarding.glossary.lectures":
    "F01–F12 och R01–R03: kursens föreläsningar och räknestugor. Kortet överst på startsidan visar vilken som är nästa.",
  "onboarding.glossary.today":
    "Dagens pass: en kort blandning av kort, repetition och något nytt, byggd efter var kursen är.",
  "onboarding.glossary.types":
    "Snabbkoll är en kort fråga (ungefär 2 minuter). Uppgift (som på tentan) är en längre uppgift som du bedömer själv.",
  "onboarding.glossary.confidence":
    "Säkerhet 1–4: hur säker du är på ditt svar. Den styr när uppgiften kommer tillbaka och kostar aldrig poäng.",
  "onboarding.glossary.principle":
    "Principsteget: du väljer först vilken princip du ska använda. Det kostar aldrig poäng.",
  "onboarding.glossary.language":
    "Sidan är på svenska. Y&F-hänvisningarna gäller den engelska boken.",
  "onboarding.glossary.more": "Engelsk ordlista under Hjälp",
  "onboarding.privacy":
    "Svaren sparas på ditt konto. Läraren ser bara sammanräknade antal för hela gruppen, aldrig dina svar.",
  "onboarding.save": "Spara",
  "onboarding.skip": "Hoppa över",
  "onboarding.saved": "Sparat. Dagens pass följer dina val.",
  "onboarding.skipped": "Hoppat över. Du kan svara senare under Inställningar.",

  "settings.onboarding.title": "Kom igång",
  "settings.onboarding.body":
    "Dina svar styr dagens pass och vilken svårighet övningen börjar med.",
  "settings.onboarding.edit": "Ändra mina val",
  "settings.onboarding.help": "Så fungerar sidan",

  "notFound.title": "Sidan finns inte",
  "notFound.body": "Länken kan vara gammal, eller så har adressen ändrats.",
  "notFound.home": "Till startsidan",

  "report.link": "Rapportera fel",
  "report.title": "Rapportera fel i uppgiften",
  "report.note": "Vad är fel?",
  "report.placeholder": "Beskriv kort vad som ser fel ut (svar, enhet, text, figur …).",
  "report.send": "Skicka",
  "report.sent": "Tack! Rapporten är skickad.",

  "source.exam": "Tenta {exam} · uppg. {number}",
  "source.homework": "Hemuppgift {number} ({term})",
  "source.practiceExam": "Övningstenta {paper} · uppg. {number}",
  "source.testbank": "Testbank",
  "source.translationPending": "Endast på svenska",

  "numeric.interpreted": "tolkas som",
  "numeric.unparseable": "kan inte tolkas som ett tal",
  "numeric.unit": "Enhet",
  "numeric.dimensionless": "ingen enhet",
  "numeric.key.minus": "Minus",
  "numeric.key.times": "Gånger tio upphöjt till",
  "numeric.key.e": "Exponent e",
  "numeric.key.comma": "Decimalkomma",
  "numeric.textKeyboard": "Textknappsats",
  "numeric.unitMismatch": "inte enheten fältet vill ha",

  "error.field.email.invalid": "Ange en giltig e-postadress.",
  "error.field.password.min": "Lösenordet måste ha minst 8 tecken.",
  "error.field.displayName.invalid": "Ange ett namn (1–80 tecken).",
  "error.field.registrationCode.wrong": "Ange rätt registreringskod.",
  "error.field.teacherCode.wrong": "Ange rätt lärarkod.",
  "error.field.generic": "Kontrollera fältet.",

  "error.VALIDATION": "Något i formuläret är fel.",
  "error.UNAUTHENTICATED": "Du behöver logga in igen.",
  "error.FORBIDDEN": "Du har inte behörighet till det här.",
  "error.CSRF": "Begäran kom inte från appen. Ladda om sidan.",
  "error.NOT_FOUND": "Hittades inte.",
  "error.EXAM_ACTIVE": "Du har redan en pågående övningstenta.",
  "error.EXAM_EXPIRED": "Skrivtiden är slut.",
  "error.EXAM_SUBMITTED": "Övningstentan är redan avslutad.",
  "error.EMAIL_TAKEN": "Det finns redan ett konto med den e-postadressen.",
  "error.EXAM_BANK_INSUFFICIENT":
    "Det finns inte tillräckligt med uppgifter för en övningstenta än.",
  "error.PAYLOAD_TOO_LARGE": "För mycket data i begäran.",
  "error.RATE_LIMITED": "För många försök. Vänta en stund.",
  "error.INTERNAL": "Något gick fel hos oss. Försök igen.",
  "error.offline": "Ingen anslutning till servern.",
} as const;

export type CoreDictionary = typeof sv;
export type CoreKey = keyof CoreDictionary;
